import { observable } from "mobx";
import ManufactureItem from "../../../model/manufacture/ManufactureItem";
import ManufactureWork from "../../../model/manufacture/ManufactureWork";
import StoreBase from "../../StoreBase";
import { ItemPresetKeyType, ItemPresetList, ItemType, ItemVersion, PresetItemPackage } from "@celemicsbisw/manufacture-item-structure";
import { ManufactureItemOption } from "../../../model/manufacture/ManufactureItemOption";

export default class ManufactureItemFormStore extends StoreBase {
    @observable data: ManufactureWork;
    @observable itemList: ManufactureItem[];
    @observable indexList: string[];
    @observable inputHeaders: (keyof ManufactureItem)[];
    @observable readonlyHeaders: (keyof ManufactureItem)[];
    @observable selectedProductType: "illumina" | "ionTorrent" | "etc" | "";

    @observable selectedItemPreset: ItemPresetKeyType;
    @observable itemPresetList: ItemPresetKeyType[];
    @observable presetItemOptions: ManufactureItemOption;
    @observable presetLoaded: boolean;
    @observable itemVersion?: ItemVersion;
    packageOptions: PresetItemPackage;

    constructor() {
        super();
        this.indexList = [];
        this.inputHeaders = [];
        this.readonlyHeaders = [];
        this.itemList = [];
        this.selectedProductType = "";
        this.selectedItemPreset = "custom";
        this.presetItemOptions = new ManufactureItemOption(false, "preset");
        this.packageOptions = null;
        this.itemPresetList = this.getPresetList();
        this.presetLoaded = false;
        this.itemVersion = null;
    }

    addNewItem = (item: ManufactureItem) => {
        this.itemList.push(item);
    };

    addNewEmptyItem = () => {
        this.itemList.push(
            new ManufactureItem({
                optionReadonly: this.readonlyHeaders.length > 0,
            })
        );
    };

    clearItems = () => {
        while (this.itemList.length > 0) {
            this.itemList.pop();
        }
    };

    resetPreset = (selectedItemPreset: ItemPresetKeyType = "custom", version?: ItemVersion) => {
        const mode = selectedItemPreset === 'btseq' ? 'btseq_preset' : 'preset';
        this.presetItemOptions = new ManufactureItemOption(false, mode);
        this.selectedItemPreset = selectedItemPreset;
    };

    getPresetList = (version?: ItemVersion) => {
        const list = Object.keys(ItemPresetList) as ItemPresetKeyType[];
        if (version && version !== 'v3') {
            return list.filter(v => v !== 'btseq');
        }
        return list;
    }

    setPresetList = (version?: ItemVersion) => {
        this.itemPresetList = this.getPresetList(version);
    }

    clearIndexList = () => {
        while (this.indexList.length > 0) {
            this.indexList.pop();
        }
    };

    setItemList = (items: ManufactureItem[]) => {
        items.forEach((v) => {
            this.itemList.push(v);
            return null;
        });
    };

    setIndexList = (list: string[]) => {
        list.forEach((v) => {
            this.indexList.push(v);
            return null;
        });
    };

    setItemListByProductType = (type: string) => {
        const list = this.getItemsBySelectedType(type);
        this.setItemList(list);
    };

    setIndexListByProductType = (type: string) => {
        const list = this.getIndexListByProductType(type);
        this.setIndexList(list);
    };

    deleteItem = (index: number) => {
        try {
            this.itemList.splice(index, 1);
        } catch (err) {
            throw err;
        }
    };

    getItemsBySelectedType = (type: string): ManufactureItem[] => {
        switch (type) {
            case "illumina":
                return this.getIlluminaItems();
            case "ionTorrent":
                return this.getIonTorrentItems();
            case "etc":
                return this.getEtcItems();
            default:
                return [];
        }
    };

    getIlluminaItems = (): ManufactureItem[] => {
        const list: ManufactureItem[] = [];
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.lp} #1`,
                no: 1,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.lp} #2`,
                no: 2,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #1`,
                no: 1,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #2`,
                no: 2,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #3`,
                no: 3,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.cleanUp,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.st,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.polymerase,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        return list;
    };

    getIonTorrentItems = (): ManufactureItem[] => {
        const list: ManufactureItem[] = [];
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.lp} #1`,
                no: 1,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.lp} #2`,
                no: 2,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #1`,
                no: 1,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #2`,
                no: 2,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: `${ManufactureItem.ItemNameList.tc} #3`,
                no: 3,
                unit: "rxn",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.cleanUp,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.st,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        list.push(
            new ManufactureItem({
                itemName: ManufactureItem.ItemNameList.polymerase,
                unit: "ml",
                numberPerQty: 16,
                validateList: this.inputHeaders,
            })
        );
        return list;
    };

    getEtcItems = (): ManufactureItem[] => {
        const list: ManufactureItem[] = [];
        return list;
    };

    getIndexListByProductType = (type: string): string[] => {
        const list: string[] = [];
        switch (type) {
            case "illumina":
                list.push("none");
                list.push("single");
                list.push("dual");
                break;
            case "illuminaEP":
                list.push("none");
                list.push("single");
                list.push("dual");
                break;
            case "illuminaCS":
                list.push("none");
                list.push("single");
                list.push("dual");
                break;
            case "ionTorrent":
                list.push("none");
                list.push("ionXpress");
                break;
            case "etc":
                list.push("none");
                list.push("single");
                list.push("dual");
                list.push("barcode");
                list.push("ionXpress");
                break;
            case "none":
                list.push("none");
                break;
            default:
                break;
        }
        return list;
    };

    itemOptionsToManufactureItem(options: ManufactureItemOption, optionReadonly: boolean = true) {
        const item = new ManufactureItem({ optionReadonly: true });
        switch (options.itemType) {
            case "lp1":
                item.itemName = "LP#1";
                item.itemOptions.lpep = options.lpep;
                break;
            case "lp2":
                item.itemName = "LP#2";
                item.itemOptions.indexType = options.indexType;
                item.itemOptions.adapterType = options.adapterType;
                break;
            case "tc1":
                item.itemName = "TC#1";
                item.itemOptions.tc1Enhancer = options.tc1Enhancer;
                item.itemOptions.tc1MethylEnhancer = options.tc1MethylEnhancer;
                item.itemOptions.indexType = options.indexType;
                if (this.presetItemOptions.version === "v3") {
                    item.itemOptions.targetSpecies = options.targetSpecies;
                    item.itemOptions.specialOptions = options.specialOptions;
                }
                break;
            case "tc2":
                item.itemName = "TC#2";
                item.itemOptions.tc2Enhancer = options.tc2Enhancer;
                item.itemOptions.tc2HybBufferPlus = options.tc2HybBufferPlus;
                break;
            case "tc3":
                item.itemName = "TC#3";
                item.itemOptions.probeState = options.probeState;
                if (options.version === "v3") {
                    item.itemOptions.probeConc = options.probeConc;
                    item.itemOptions.panelSize = options.panelSize;
                    item.itemOptions.specialOptions = options.specialOptions;
                    item.itemOptions.pooling = options.pooling;
                }
                break;
            case 'bt001':
                item.itemName = 'BT001';
                item.itemOptions.itemType = 'bt001';
                item.itemOptions.packagingType = this.presetItemOptions.packagingType1;
                break;
            case 'bt002':
                item.itemName = 'BT002'
                item.itemOptions.itemType = 'bt002';
                item.itemOptions.packagingType = this.presetItemOptions.packagingType2;
                break;
            case 'bt003':
                item.itemName = 'BT003';
                item.itemOptions.itemType = 'bt003';
                item.itemOptions.btseqIndexType = this.presetItemOptions.btseqIndexType;
                break;
            case "cDNA":
                item.itemName = "cDNA Synthesis Kit";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmBead":
                item.itemName = "NM bead";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmCelanUpBead":
                item.itemName = "Clean-up bead";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmStreptavidinBead":
                item.itemName = "Streptavidin bead";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmPolymerase":
                item.itemName = "Polymerase";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmBeadBindingBuffer":
                item.itemName = "NM bead binding buffer";
                item.itemOptions.probeState = options.probeState;
                break;
            case "accClmBeadAndBufferSet":
                item.itemName = "NM Bead Set";
                item.itemOptions.probeState = options.probeState;
                break;
            default:
                break;
        }
        item.itemOptions.platform = options.platform;
        item.itemOptions.readonly = optionReadonly;
        item.version = options.version;
        item.itemOptions.version = options.version;
        this.setPresetOptionsToItem(item, options.itemType);
        return item;
    }

    setPresetOptionsToItem = (item: ManufactureItem, itemType: ItemType) => {
        switch (itemType) {
            case "lp1":
            case "lp2":
            case 'bt001':
            case 'bt002':
            case 'bt003':
                item.unit = this.presetItemOptions.unit;
                item.numberPerQty = this.presetItemOptions.numberPerQty;
                item.qty = this.presetItemOptions.qty;
                break;
            case "tc1":
            case "tc2":
            case "tc3":
            case "accClmBead":
            case "accClmBeadBindingBuffer":
            case "accClmBeadAndBufferSet":
                item.unit = this.presetItemOptions.unit;
                item.numberPerQty = this.presetItemOptions.numberPerQty;
                item.qty = this.presetItemOptions.qty;
                item.pooling = this.presetItemOptions.pooling;
                break;
            case "cDNA":
                item.numberPerQty = this.presetItemOptions.numberPerQty;
                item.unit = this.presetItemOptions.unit;
                item.qty = this.presetItemOptions.qty;
                break;
            case "accClmCelanUpBead":
            case "accClmStreptavidinBead":
            case "accClmPolymerase":
                switch (this.presetItemOptions.version) {
                    case "v1":
                    case "v2":
                        item.unit = this.presetItemOptions.unit;
                        item.numberPerQty = this.presetItemOptions.numberPerQty;
                        item.qty = this.presetItemOptions.qty;
                        item.pooling = this.presetItemOptions.pooling;
                        break;
                    case "v3":
                        item.unit = "ml";
                        item.qty = this.presetItemOptions.qty;
                        break;
                    default:
                        break;
                }
                break;
            default:
                break;
        }
        return item;
    };

    getPresetItemList = () => {
        const pack = new PresetItemPackage({
            platform: this.presetItemOptions.platform,
            adapter: this.presetItemOptions.adapterType,
            lpep: this.presetItemOptions.lpep,
            indexType: this.presetItemOptions.indexType,
            tc1Enhancer: this.presetItemOptions.tc1Enhancer,
            tc1MethylEnhancer: this.presetItemOptions.tc1MethylEnhancer,
            tc2Enhancer: this.presetItemOptions.tc2Enhancer,
            tc2HybBufferPlus: this.presetItemOptions.tc2HybBufferPlus,
            probeState: this.presetItemOptions.probeState,
            selectedItemPreset: this.selectedItemPreset,
            probeConc: this.presetItemOptions.probeConc,
            itemVersion: this.itemVersion,
            targetSpecies: this.presetItemOptions.targetSpecies,
            panelSize: this.presetItemOptions.panelSize,
            specialOptions: this.presetItemOptions.specialOptions,
        });
        pack.setItemListBySelection();
        pack.syncAllToItems();
        this.packageOptions = pack;
        const list = pack.itemList.map((v) => {
            const options = new ManufactureItemOption(true, "options");
            switch (v.type) {
                case "lp1":
                    options.itemType = "lp1";
                    options.platform = pack.platform;
                    options.lpep = pack.lpep;
                    break;
                case "lp2":
                    options.itemType = "lp2";
                    options.platform = pack.platform;
                    options.indexType = pack.indexType;
                    options.adapterType = pack.adapter;
                    break;
                case "tc1":
                    options.itemType = "tc1";
                    options.platform = pack.platform;
                    options.tc1Enhancer = pack.tc1Enhancer;
                    options.tc1MethylEnhancer = pack.itemVersion === 'v3' && pack.adapter === 'methyl'
                        ? true
                        : pack.tc1MethylEnhancer;
                    options.indexType = pack.indexType;
                    options.targetSpecies = pack.targetSpecies;
                    if (pack.itemVersion === "v3") {
                        options.specialOptions = pack.specialOptions;
                    }
                    break;
                case "tc2":
                    options.itemType = "tc2";
                    options.platform = pack.platform;
                    options.tc2Enhancer = pack.tc2Enhancer;
                    if (pack.itemVersion === 'v3') {
                        options.tc2HybBufferPlus = pack.tc2HybBufferPlus;
                        if (!options.tc2Enhancer && !options.tc2HybBufferPlus) {
                            options.selectedHybEnhancer = 'etc';
                        }
                    }
                    else options.tc2HybBufferPlus = false;
                    break;
                case "tc3":
                    options.itemType = "tc3";
                    options.platform = pack.platform;
                    options.probeState = pack.probeState;
                    options.probeConc = pack.probeConc;
                    if (pack.itemVersion === "v3") {
                        options.probeConc = pack.probeConc;
                        options.panelSize = pack.panelSize;
                        options.specialOptions = pack.specialOptions;
                        options.pooling = Number(this.presetItemOptions.pooling);
                    }
                    break;
                case 'bt001':
                    options.itemType = 'bt001';
                    options.packagingType = this.presetItemOptions.packagingType1;
                    break;
                case 'bt002':
                    options.itemType = 'bt002';
                    options.packagingType = this.presetItemOptions.packagingType2;
                    break;
                case 'bt003':
                    options.itemType = 'bt003';
                    options.btseqIndexType = this.presetItemOptions.btseqIndexType;
                    break;
                case "cDNA":
                    options.itemType = "cDNA";
                    options.platform = pack.platform;
                    break;
                case "accClmBead":
                    options.itemType = "accClmBead";
                    options.platform = pack.platform;
                    break;
                case "accClmCelanUpBead":
                    options.itemType = "accClmCelanUpBead";
                    options.platform = pack.platform;
                    break;
                case "accClmStreptavidinBead":
                    options.itemType = "accClmStreptavidinBead";
                    options.platform = pack.platform;
                    break;
                case "accClmPolymerase":
                    options.itemType = "accClmPolymerase";
                    options.platform = pack.platform;
                    break;
                case "accClmBeadBindingBuffer":
                    options.itemType = "accClmBeadBindingBuffer";
                    options.platform = pack.platform;
                    break;
                case "accClmBeadAndBufferSet":
                    options.itemType = "accClmBeadAndBufferSet";
                    options.platform = pack.platform;
                    break;
                default:
                    break;
            }
            options.version = this.itemVersion;
            return this.itemOptionsToManufactureItem(options, false);
        });
        return list;
    };

    onPresetItemGenerate = (mode: "add" | "gen") => {
        this.presetItemOptions.resetErrorsRecursive();
        this.presetItemOptions.validateSelf();
        if (this.presetItemOptions.hasError()) {
            return;
        } else {
            if (mode === "gen") {
                this.clearItems();
            }
            const list = this.getPresetItemList();
            list.forEach((v) => this.itemList.push(v));
        }
    };
}
